'use client';

import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useRouter } from 'next/navigation';
import { type ReactNode, useEffect, useMemo } from 'react';

import { AppAction } from '../../../contexts/TokenContext/Roles';
import { useIsAllowed } from '../../../contexts/TokenContext/useIsAllowed';
import { useRoles } from '../../../contexts/TokenContext/useRoles';
import { LoadingProgressBar } from './reportProblem/_components/SaveProgressBar';

export default function Page(): ReactNode {
	const router = useRouter();
	const hasAccessToSummary = useIsAllowed(AppAction.Summary);
	const hasAccessToPremises = useIsAllowed(AppAction.SearchOrders);
	const roles = useRoles();
	const { inProgress } = useMsal();

	const waitingMessage = useMemo(() => {
		if (!router) {
			return 'Initializing page...';
		}
		if (inProgress !== InteractionStatus.None) {
			return 'Authenticating...';
		}
		if (roles === undefined) {
			return 'Checking permissions...';
		}

		return undefined;
	}, [router, roles, inProgress]);

	useEffect(() => {
		// Router isn't ready yet
		if (!router) {
			return;
		}

		// Roles aren't ready yet
		if (roles === undefined) {
			return;
		}

		// If they have access to the summary page, use that as the "default"
		if (hasAccessToSummary) {
			router.replace('/summary/');
			return;
		}

		// If they have access to the premises page, use that as the backup
		if (hasAccessToPremises) {
			router.replace('/premises/');
			return;
		}

		// If the user has roles, but doesn't have access to either page display an error
		router.replace('/noAccess/');
	}, [roles, router, hasAccessToPremises, hasAccessToSummary]);

	return (
		<>
			<br />
			<LoadingProgressBar>{waitingMessage}</LoadingProgressBar>
		</>
	);
}
